import { type DraggableProvided } from 'react-beautiful-dnd'
import { type FC, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { Icon, Typography } from 'shared/ui'
import { IntegrationKey } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { type ContactsIntegrationsStore } from 'widgets/ContactsDetails/ui/ContactsIntegrations'
import {
  ProviderContactsIntegrations,
  useContactsIntegrationsContext,
} from 'widgets/ContactsDetails/ui/ContactsIntegrations/context/contactsIntegrationsContext'
import { IntegrationTitle } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/IntegrationTitle/IntegrationTitle'
import { NangoIntegration } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/NangoIntegration/NangoIntegration'
import { HubspotIntegration } from './HubspotIntegration/HubspotIntegration'
import { ActiveCampaignIntegration } from './ActiveCampaignIntegration/ActiveCampaignIntegration'
import { PipedriveIntegration } from './PipedriveIntegration/PipedriveIntegration'
import { InfusionsoftIntegration } from './InfusionsoftIntegration/InfusionsoftIntegration'
import { UnlinkActions } from './UnlinkActions/UnlinkActions'
import styles from './styles.module.scss'
import { AllCompanies, AllDeals, AllOpportunities, AllTickets } from './AllView'
import { LinkContact } from './LinkContact'
import { SalesforceIntegration } from './SalesforceIntegration/SalesforceIntegration'

export type IContactsIntegrationsContentProps = {
  isDragging?: boolean
  createNewContactMode?: boolean
  hideDraggingIcon?: boolean
  dragHandleProps?: DraggableProvided['dragHandleProps']
  headerStyles?: Record<string, string>
}

const ContactsIntegrationsContent: FC<IContactsIntegrationsContentProps> = observer(
  ({ isDragging, createNewContactMode, hideDraggingIcon, dragHandleProps, headerStyles }) => {
    const store = useContactsIntegrationsContext()
    const { contactsStore, usersStore, integrationsStore } = useEntitiesContext()
    const [isOpenActions, setIsOpenActions] = useState(false)

    const user = usersStore.user
    const contact = store.selectedContact?.isNew
      ? store.selectedContact
      : contactsStore.getItem(store.selectedContact?.id)

    useEffect(() => {
      integrationsStore.fetchIntegrations()

      return () => {
        store.dispose()
      }
    }, [])

    const integration = integrationsStore.getIntegration(contact?.integration_key)
    const isIntegrationActive = Boolean(integration?.isConnected)
    const disabledToggle = integration?.isNangoProvider

    const onToggle = () => {
      if (createNewContactMode) return
      if (disabledToggle) return
      contactsDetailsGlobalStore.toggleBlockView(store.integrationBlockViewKey)
    }

    const Integration = useMemo(() => {
      if (!contact) return null

      if (!isIntegrationActive && contact.integration_key === IntegrationKey.salesmsg) {
        return <LinkContact contact={contact} />
      }

      if (contact.integration_key === IntegrationKey.hubspot) {
        return <HubspotIntegration />
      }

      if (contact.integration_key === IntegrationKey.activecampaign) {
        return <ActiveCampaignIntegration />
      }

      if (contact.integration_key === IntegrationKey.pipedrive) {
        return <PipedriveIntegration />
      }

      if (contact.integration_key === IntegrationKey.infusionsoft) {
        return <InfusionsoftIntegration />
      }

      if (contact.integration_key === IntegrationKey.salesforce) {
        return <SalesforceIntegration />
      }
      if (integration?.isNangoProvider) {
        return <NangoIntegration integration={integration} />
      }

      return (
        <Typography variant={'body-md-regular'} ariaLabel={'name'}>
          {integration?.name}
        </Typography>
      )
    }, [integration, contact])

    if (!contact) return null

    if (store.view === 'deals') {
      return <AllDeals />
    }

    if (store.view === 'companies') {
      return <AllCompanies />
    }

    if (store.view === 'opportunities') {
      return <AllOpportunities />
    }

    if (store.view === 'tickets') {
      return <AllTickets />
    }

    return (
      <div
        className={classnames(styles.wrap, {
          [styles.dragging]: isDragging,
          [styles.disabled]: createNewContactMode,
        })}
      >
        <div
          className={classnames(styles.header, {
            [styles.initialCursor]: disabledToggle,
            [styles.open]: createNewContactMode
              ? false
              : contactsDetailsGlobalStore.isOpenBlock(store.integrationBlockViewKey),
          })}
          style={{ ...headerStyles }}
          onClick={onToggle}
        >
          <IntegrationTitle
            integration={integration}
            hideDraggingIcon={hideDraggingIcon}
            dragHandleProps={dragHandleProps}
            isIntegrationActive={isIntegrationActive}
            contact={contact}
            createNewContactMode={createNewContactMode}
            integrationBlockViewKey={store.integrationBlockViewKey}
          />
          <div className={styles.actions}>
            {!!integration && isIntegrationActive && !user?.isViewOnlyRole && (
              <div
                className={classnames(styles.actionsIcon, {
                  [styles.open]: isOpenActions,
                })}
              >
                <UnlinkActions contact={contact} setStatus={setIsOpenActions} />
              </div>
            )}
            {!disabledToggle && (
              <div className={styles.toggleIcon}>
                <Icon icon='chevronDown' />
              </div>
            )}
          </div>
        </div>
        {(contactsDetailsGlobalStore.isOpenBlock(store.integrationBlockViewKey) ||
          (disabledToggle && !integration?.connected)) && (
          <div className={classnames(styles.body, styles.open)}>{Integration}</div>
        )}
      </div>
    )
  }
)

export type IContactsIntegrationsProps = {
  store: ContactsIntegrationsStore
  isDragging?: boolean
  createNewContactMode?: boolean
  hideDraggingIcon?: boolean
  dragHandleProps?: DraggableProvided['dragHandleProps']
  headerStyles?: Record<string, string>
}

export const ContactsIntegrations: FC<IContactsIntegrationsProps> = observer(
  ({
    store,
    isDragging,
    createNewContactMode,
    hideDraggingIcon,
    dragHandleProps,
    headerStyles,
  }) => {
    if (!store.selectedContact) return <div {...dragHandleProps}></div>

    return (
      <ProviderContactsIntegrations value={store}>
        <ContactsIntegrationsContent
          key={store.selectedContact.id}
          createNewContactMode={createNewContactMode}
          isDragging={isDragging}
          hideDraggingIcon={hideDraggingIcon}
          dragHandleProps={dragHandleProps}
          headerStyles={headerStyles}
        />
      </ProviderContactsIntegrations>
    )
  }
)
